import * as THREE from 'three';
import { makeSprite } from './NetworkField';

/**
 * ambient — slow spherical dust, the resting state
 * drift   — turbulent, uncoordinated motion (the problem)
 * orbit   — calm concentric rings around the centre (organised systems)
 * inward  — a slow spiral into the centre (convergence)
 * stream  — lanes flowing left to right (information and activity in motion)
 */
export type ParticlePattern = 'ambient' | 'drift' | 'orbit' | 'inward' | 'stream';

export class ParticleField {
  public readonly points: THREE.Points;

  private readonly count: number;
  private readonly seeds: Float32Array;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsMaterial;
  private readonly fromPosition = new THREE.Vector3();
  private readonly toPosition = new THREE.Vector3();
  private readonly targetColor = new THREE.Color(0x53f5c7);

  private pattern: ParticlePattern = 'ambient';
  private targetPattern: ParticlePattern = 'ambient';
  private transition = 1;
  private density = 0.35;
  private targetDensity = 0.35;
  private targetOpacity = 0.46;
  private targetSize = 0.095;

  constructor(count = 1200) {
    this.count = count;
    this.seeds = new Float32Array(count * 4);
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i += 1) {
      const i4 = i * 4;
      this.seeds[i4] = Math.random();
      this.seeds[i4 + 1] = Math.random();
      this.seeds[i4 + 2] = Math.random();
      this.seeds[i4 + 3] = Math.random();
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    this.material = new THREE.PointsMaterial({
      size: 0.095,
      map: makeSprite(),
      color: 0x53f5c7,
      transparent: true,
      opacity: 0.46,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.geometry.setDrawRange(0, Math.floor(this.count * this.density));
  }

  public setColor(color: string): void {
    this.targetColor.set(color);
  }

  public setPattern(pattern: ParticlePattern): void {
    if (pattern === this.targetPattern) return;
    this.pattern = this.targetPattern;
    this.targetPattern = pattern;
    this.transition = 0;
  }

  public setOpacity(value: number): void {
    this.targetOpacity = THREE.MathUtils.clamp(value, 0, 1);
  }

  public setDensity(value: number): void {
    this.targetDensity = THREE.MathUtils.clamp(value, 0.03, 1);
  }

  public setSize(value: number): void {
    this.targetSize = THREE.MathUtils.clamp(value, 0.02, 0.2);
  }

  public update(time: number, delta: number): void {
    const transitionRate = 2.9;
    const propertyRate = Math.min(1, delta * 5.5);
    this.transition = Math.min(1, this.transition + delta * transitionRate);
    this.density = THREE.MathUtils.lerp(this.density, this.targetDensity, propertyRate);
    this.material.opacity = THREE.MathUtils.lerp(this.material.opacity, this.targetOpacity, propertyRate);
    this.material.size = THREE.MathUtils.lerp(this.material.size, this.targetSize, propertyRate);
    this.material.color.lerp(this.targetColor, propertyRate);

    const drawCount = Math.floor(this.count * this.density);
    const calculationCount = Math.min(this.count, Math.ceil(this.count * Math.max(this.density, this.targetDensity)) + 8);
    this.geometry.setDrawRange(0, drawCount);

    const eased = this.transition * this.transition * (3 - 2 * this.transition);
    const pos = this.geometry.attributes.position.array as Float32Array;

    // Reuse two Vector3 instances for the whole frame. The previous implementation created
    // thousands of Vector3 objects every frame, causing avoidable GC spikes during transitions.
    for (let i = 0; i < calculationCount; i += 1) {
      const i3 = i * 3;
      this.positionFor(this.pattern, i, time, this.fromPosition);
      this.positionFor(this.targetPattern, i, time, this.toPosition);
      pos[i3] = THREE.MathUtils.lerp(this.fromPosition.x, this.toPosition.x, eased);
      pos[i3 + 1] = THREE.MathUtils.lerp(this.fromPosition.y, this.toPosition.y, eased);
      pos[i3 + 2] = THREE.MathUtils.lerp(this.fromPosition.z, this.toPosition.z, eased);
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.points.rotation.y = Math.sin(time * 0.08) * 0.025;
  }

  private positionFor(pattern: ParticlePattern, index: number, time: number, out: THREE.Vector3): THREE.Vector3 {
    const i4 = index * 4;
    const a = this.seeds[i4];
    const b = this.seeds[i4 + 1];
    const c = this.seeds[i4 + 2];
    const d = this.seeds[i4 + 3];

    if (pattern === 'drift') {
      return out.set(
        (a - 0.5) * 24 + Math.sin(time * (0.35 + c * 0.4) + d * 20) * 1.1,
        (b - 0.5) * 13 + Math.cos(time * (0.3 + d * 0.35) + c * 17) * 0.8,
        (c - 0.5) * 8,
      );
    }

    if (pattern === 'orbit') {
      const ring = index % 4;
      const theta = a * Math.PI * 2 + time * (0.028 + ring * 0.009);
      const r = 3.9 + ring * 1.85 + b * 0.45;
      return out.set(Math.cos(theta) * r, Math.sin(theta) * r * 0.42 + (c - 0.5) * 0.3, (d - 0.5) * 1.4 - ring * 0.2);
    }

    if (pattern === 'stream') {
      const lane = Math.floor(a * 9) - 4;
      const phase = (b + time * (0.05 + c * 0.07)) % 1;
      return out.set(-12 + phase * 24, lane * 0.9 + Math.sin(time * 0.8 + d * 12) * 0.12, (c - 0.5) * 3);
    }

    if (pattern === 'inward') {
      const phase = (b + time * (0.1 + c * 0.05)) % 1;
      const radius = 10 * (1 - phase) + 0.3;
      const theta = a * Math.PI * 2 + phase * Math.PI * 2.6;
      return out.set(Math.cos(theta) * radius, Math.sin(theta) * radius * 0.5, (d - 0.5) * radius * 0.22);
    }

    const theta = a * Math.PI * 2 + time * 0.024;
    const phi = Math.acos(2 * b - 1);
    const r = 7.5 + c * 8.5;
    return out.set(r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi) * 0.58, r * Math.sin(phi) * Math.sin(theta));
  }

  public dispose(): void {
    this.geometry.dispose();
    this.material.map?.dispose();
    this.material.dispose();
  }
}
